import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const { login, expiredNotice, clearExpiredNotice } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      clearExpiredNotice();
      const dest = location.state?.from || '/heute';
      navigate(dest, { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const code = err.response?.data?.error;
      if (status === 429 || code === 'too_many_attempts') {
        setError(t('auth.tooMany'));
      } else if (code === 'temp_password_expired') {
        setError(t('auth.tempPasswordExpired'));
      } else if (status === 401) {
        setError(t('auth.invalid'));
      } else if (!err.response) {
        setError(t('errors.network'));
      } else {
        const msg = err.response?.data?.message;
        setError(typeof msg === 'string' ? msg : t('errors.unknown'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function switchLang(l) {
    i18n.changeLanguage(l);
    localStorage.setItem('studioops_lang', l);
  }

  const currentLang = i18n.language?.startsWith('en') ? 'en' : 'de';

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="login-page">
      {/* Top bar with language switch */}
      <header className="flex items-center justify-between px-6 py-5 md:px-10">
        <Logo variant="dark" />
        <div className="flex items-center gap-1 rounded-full border border-border bg-white p-1 text-xs font-semibold" data-testid="language-switcher">
          <button
            type="button"
            onClick={() => switchLang('de')}
            className={`rounded-full px-3 py-1 transition ${currentLang === 'de' ? 'bg-navy text-white' : 'text-text-secondary hover:text-text'}`}
            data-testid="lang-de-btn"
          >
            DE
          </button>
          <button
            type="button"
            onClick={() => switchLang('en')}
            className={`rounded-full px-3 py-1 transition ${currentLang === 'en' ? 'bg-navy text-white' : 'text-text-secondary hover:text-text'}`}
            data-testid="lang-en-btn"
          >
            EN
          </button>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-6 py-8">
        <div className="w-full max-w-[440px]">
          <div className="so-card p-8 md:p-10" data-testid="login-card">
            <h1 className="text-2xl font-bold text-text tracking-tight" data-testid="login-title">
              {t('auth.loginTitle')}
            </h1>
            <p className="mt-2 text-sm text-text-secondary">{t('auth.loginSubtitle')}</p>

            {expiredNotice && (
              <div className="mt-5 rounded-md border border-warning/30 bg-warning/10 px-3 py-2.5 text-sm text-warning" data-testid="expired-notice">
                <strong className="font-semibold">{t('auth.expiredTitle')}: </strong>
                {t('auth.expiredText')}
              </div>
            )}

            <form className="mt-6 space-y-4" onSubmit={handleSubmit} data-testid="login-form" noValidate>
              <div>
                <label className="so-label" htmlFor="email">{t('auth.email')}</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="so-input"
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <label className="so-label" htmlFor="password">{t('auth.password')}</label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPwd ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="so-input pr-20"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-1 text-xs font-semibold text-text-secondary hover:text-text"
                    aria-label="toggle password visibility"
                    data-testid="login-password-toggle"
                  >
                    {showPwd ? '••' : 'AB'}
                  </button>
                </div>
              </div>

              {error && (
                <div className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2.5 text-sm text-danger" data-testid="login-error">
                  {error}
                </div>
              )}

              <div className="text-right -mt-1">
                <a
                  href="/passwort-vergessen"
                  className="text-xs text-accent hover:underline"
                  data-testid="forgot-password-link"
                  onClick={(e) => { e.preventDefault(); navigate('/passwort-vergessen'); }}
                >
                  {t('auth.forgotPassword')}
                </a>
              </div>

              <button
                type="submit"
                className="so-btn so-btn-primary w-full"
                disabled={submitting}
                data-testid="login-submit-btn"
              >
                {submitting ? t('auth.submitting') : t('auth.submit')}
              </button>
            </form>
          </div>

          {/* DSGVO footer */}
          <p className="mt-6 text-center text-xs text-text-secondary px-4" data-testid="dsgvo-notice">
            {t('auth.dsgvoNotice')}{' '}
            <a href="#datenschutz" className="so-link" data-testid="datenschutz-link">
              {t('auth.datenschutzLink')}
            </a>
            {t('auth.dsgvoNoticeSuffix')}
          </p>
          <p className="mt-2 text-center text-[11px] text-text-secondary">
            {t('auth.footerNote', { year: new Date().getFullYear() })}
          </p>
        </div>
      </main>
    </div>
  );
}
